// The selected structure in the scene's runs, for the inspector's sim fields (spec §6, §15): its value
// now in each overlay drawn on it, its column for a sparkline, and, in a pharma run, its receptor density.

import type { DynamicsSim } from './dynamics.ts';
import type { HemoSegment, HemoSim } from './hemo.ts';
import type { Overlay } from './overlay.ts';
import { duration, frameTimes, sampleAt, type TimeAxis } from './time.ts';

export interface SimRow {
  overlay: Overlay;
  /** The structure's column in the overlay. */
  key: number;
  now: number;
}

/** Each overlay keyed by the structure, with its value at `t`; a value that's missing (NaN) has no row. */
export function simRowsOf(overlays: Overlay[], structure: string, t: number): SimRow[] {
  return overlays.flatMap((overlay) => {
    const key = overlay.keys.indexOf(structure);
    if (key < 0) return [];
    const now = overlay.times.length > 1 ? sampleAt(overlay.values, overlay.keys.length, key, overlay.axis, t) : overlay.values[key]!;
    return Number.isFinite(now) ? [{ overlay, key, now }] : [];
  });
}

const columns = new WeakMap<Overlay, Map<number, Float32Array>>();

/** One key's column of an overlay, `[frame]`, kept per overlay so a sparkline isn't rebuilt each frame. */
export function columnOf(o: Overlay, key: number): Float32Array {
  let byKey = columns.get(o);
  if (!byKey) columns.set(o, (byKey = new Map()));
  let y = byKey.get(key);
  if (!y) {
    const K = o.keys.length;
    y = Float32Array.from({ length: o.times.length }, (_, f) => o.values[f * K + key]!);
    byKey.set(key, y);
  }
  return y;
}

/** Where `t` falls along an overlay's frames, 0 to 1, for a sparkline's playhead; a periodic run wraps. */
export function along(o: { times: ArrayLike<number>; axis: TimeAxis }, t: number): number | null {
  const n = o.times.length;
  if (n < 2) return null;
  const t0 = o.times[0]!;
  const span = o.times[n - 1]! - t0;
  if (!(span > 0)) return null;
  const period = o.axis.periodic && o.axis.period ? o.axis.period : 0;
  const x = period ? (((t - t0) % period) + period) % period : t - t0;
  return Math.min(1, Math.max(0, x / span));
}

/**
 * A region's receptor density from an engagement overlay. Engagement is occupancy × density, and a region's
 * value is the mean of its parcels' in the same frame, where occupancy is the same everywhere: so its density
 * is its engagement over the occupancy. It's read at the course's peak occupancy. `below` is the share of the
 * other keys with a lower density, for "among the densest".
 */
export function regionDensity(o: Overlay, key: number, occupancy: ArrayLike<number>): { value: number; below: number } | null {
  let peak = 0;
  for (let f = 1; f < occupancy.length; f++) if (occupancy[f]! > occupancy[peak]!) peak = f;
  const occ = occupancy[peak]!;
  if (!(occ > 0) || peak >= o.times.length) return null;
  const K = o.keys.length;
  const value = o.values[peak * K + key]! / occ;
  if (!Number.isFinite(value)) return null;
  const all = Array.from({ length: K }, (_, j) => o.values[peak * K + j]! / occ).filter(Number.isFinite);
  return { value, below: all.filter((v) => v < value).length / Math.max(1, all.length) };
}

/** A vessel's quantities over a 1D run, `[frame]` each; radius from the lumen's area. */
export interface VesselSeries {
  flow?: Float32Array;
  pressure?: Float32Array;
  velocity?: Float32Array;
  radius?: Float32Array;
}

/** The length-weighted mean of the segments' arc means, as `HemoSim.structureSeries` gives the overlay. */
function structureColumn(h: HemoSim, data: Float32Array, segs: HemoSegment[], abs: boolean): Float32Array {
  const F = h.axis.frames;
  const C = h.columns;
  const total = segs.reduce((a, s) => a + s.lengthMm, 0) || 1;
  const out = new Float32Array(F);
  for (const seg of segs) {
    const { offset, count } = seg.samples;
    const w = seg.lengthMm / total / count;
    for (let f = 0; f < F; f++) {
      let sum = 0;
      for (let k = 0; k < count; k++) {
        const v = data[f * C + offset + k]!;
        sum += abs ? Math.abs(v) : v;
      }
      out[f] = out[f]! + sum * w;
    }
  }
  return out;
}

/**
 * A structure's flow, pressure, velocity and radius over a 1D run, read as its overlay colours it: flow
 * and velocity as magnitudes, the structure the length-weighted mean of its segments. Radius is
 * √(A/π) of the mean lumen area, given in mm² (else there's none). Null without segments.
 */
export function vesselSeries(h: HemoSim, data: Partial<Record<'flow' | 'pressure' | 'velocity' | 'area', Float32Array>>, structure: string): VesselSeries | null {
  const segs = h.segmentsFor(structure);
  if (!segs.length) return null;
  const out: VesselSeries = {};
  if (data.flow) out.flow = structureColumn(h, data.flow, segs, true);
  if (data.pressure) out.pressure = structureColumn(h, data.pressure, segs, false);
  if (data.velocity) out.velocity = structureColumn(h, data.velocity, segs, true);
  if (data.area && /^mm(2|\^2|²)$/.test(h.unit('area'))) out.radius = structureColumn(h, data.area, segs, false).map((a) => Math.sqrt(Math.max(0, a) / Math.PI));
  return out;
}

export interface TerritoryShare {
  /** The outlet's territory id, `l-mca-frontal`. */
  territory: string;
  /** The share of the territory's inflow that came through the structure, 0–1. */
  share: number;
  /** That inflow, in the run's flow units. */
  flow: number;
}

/**
 * Where a vessel's blood goes in a 1D run: each outlet territory's share of its mean inflow that came
 * through the structure's segments. Each segment carries its mean flow over the run, in the direction
 * of that mean, and the blood mixes fully at each junction. Largest flow first; null when the structure
 * has no segments or the mean flows don't form a network that drains to its outlets.
 */
export function territoriesOf(h: HemoSim, flow: Float32Array, structure: string): TerritoryShare[] | null {
  const own = new Set(h.segmentsFor(structure).map((s) => s.id));
  if (!own.size) return null;
  const F = h.axis.frames;
  const C = h.columns;
  const all = h.segments.map((seg) => {
    const { offset, count } = seg.samples;
    let sum = 0;
    for (let f = 0; f < F; f++) for (let k = 0; k < count; k++) sum += flow[f * C + offset + k]!;
    const q = sum / (F * count);
    return { seg, q: Math.abs(q), from: String(q >= 0 ? seg.sn : seg.tn), to: String(q >= 0 ? seg.tn : seg.sn) };
  });
  const top = all.reduce((m, e) => Math.max(m, e.q), 0);
  // A segment with no mean flow (an occluded one, or round-off) carries nothing either way.
  const edges = all.filter((e) => e.seg.state !== 'absent' && e.q > top * 1e-6);
  const into = new Map<string, typeof edges>();
  const outOf = new Map<string, typeof edges>();
  for (const e of edges) {
    into.set(e.to, [...(into.get(e.to) ?? []), e]);
    outOf.set(e.from, [...(outOf.get(e.from) ?? []), e]);
  }
  // Junctions in flow order (Kahn); mean flows down a pressure gradient have no cycle.
  const waiting = new Map<string, number>();
  for (const e of edges) waiting.set(e.to, (waiting.get(e.to) ?? 0) + 1);
  const nodes = new Set(edges.flatMap((e) => [e.from, e.to]));
  const ready = [...nodes].filter((n) => !waiting.get(n));
  const share = new Map<(typeof edges)[number], number>();
  let done = 0;
  while (ready.length) {
    const n = ready.pop()!;
    const ins = into.get(n) ?? [];
    const q = ins.reduce((a, e) => a + e.q, 0);
    const c = q > 0 ? ins.reduce((a, e) => a + e.q * share.get(e)!, 0) / q : 0;
    for (const e of outOf.get(n) ?? []) {
      share.set(e, own.has(e.seg.id) ? 1 : c);
      done++;
      const left = waiting.get(e.to)! - 1;
      waiting.set(e.to, left);
      if (!left) ready.push(e.to);
    }
  }
  if (done !== edges.length) return null;
  const byTerritory = new Map<string, { inflow: number; ours: number }>();
  for (const e of edges) {
    const t = e.seg.territory;
    if (!t) continue;
    const row = byTerritory.get(t) ?? { inflow: 0, ours: 0 };
    row.inflow += e.q;
    row.ours += e.q * share.get(e)!;
    byTerritory.set(t, row);
  }
  return [...byTerritory]
    .map(([territory, r]) => ({ territory, share: r.inflow > 0 ? r.ours / r.inflow : 0, flow: r.ours }))
    .filter((t) => t.flow > 0)
    .sort((a, b) => b.flow - a.flow);
}

const ARTERY: Record<string, string> = {
  aca: 'ACA',
  mca: 'MCA',
  pca: 'PCA',
  sca: 'SCA',
  aica: 'AICA',
  pica: 'PICA',
  acha: 'anterior choroidal',
  ophthalmic: 'ophthalmic',
  'lateral-lenticulostriate': 'lateral lenticulostriates',
  'medial-lenticulostriate': 'medial lenticulostriates',
  'posterior-choroidal-thalamoperforators': 'posterior choroidal and thalamoperforators',
  'anterior-choroidal-thalamoperforators': 'anterior choroidal and thalamoperforators',
};
const REGIONS = ['frontal', 'parietal', 'temporal', 'occipital', 'insular'];

/**
 * Territory ids as a line: grouped by artery, its regions in brackets, the side said once where every
 * group has the same: "Left MCA (frontal, parietal, temporal), lateral lenticulostriates",
 * "Both sides: PCA (temporal, occipital), SCA".
 */
export function territoryLine(ids: string[]): string {
  const groups = new Map<string, { sides: Set<string>; regions: Set<string> }>();
  for (const id of ids) {
    const m = /^([lr])-(.+)$/.exec(id);
    const side = m?.[1] ?? '';
    const rest = m?.[2] ?? id;
    const r = /^(aca|mca|pca)-(\w+)$/.exec(rest);
    const artery = r && REGIONS.includes(r[2]!) ? r[1]! : rest;
    const g = groups.get(artery) ?? { sides: new Set(), regions: new Set() };
    g.sides.add(side);
    if (r && REGIONS.includes(r[2]!)) g.regions.add(r[2]!);
    groups.set(artery, g);
  }
  const sideOf = (s: Set<string>) => (s.has('l') && s.has('r') ? 'Both sides' : s.has('l') ? 'Left' : s.has('r') ? 'Right' : '');
  const name = (artery: string, g: { regions: Set<string> }) => {
    const regions = REGIONS.filter((x) => g.regions.has(x));
    return `${ARTERY[artery] ?? artery.replace(/-/g, ' ')}${regions.length ? ` (${regions.join(', ')})` : ''}`;
  };
  const list = [...groups];
  const sides = new Set(list.map(([, g]) => sideOf(g.sides)));
  if (sides.size === 1) {
    const side = [...sides][0]!;
    const body = list.map(([a, g]) => name(a, g)).join(', ');
    return side === 'Both sides' ? `Both sides: ${body}` : side ? `${side} ${body}` : body;
  }
  return list
    .map(([a, g]) => {
      const side = sideOf(g.sides);
      return side === 'Both sides' ? `${name(a, g)} on both sides` : `${side} ${name(a, g)}`.trim();
    })
    .join(', ')
    .replace(/^./, (c) => c.toUpperCase());
}

/** The territories a vessel mainly supplies: those that take at least half their blood through it. */
export const mainTerritories = (shares: TerritoryShare[]): TerritoryShare[] => shares.filter((t) => t.share >= 0.5);

/**
 * The activity a seizure run counts as seizing from: half its colour range. Epileptor's envelope is
 * bimodal, so the level hardly moves the times. In the default Seizure run 63 of 82 parcels never pass a
 * tenth of the range and the other 19 pass all of it, and a quarter of the range gives every
 * recruitment time within 0.1 s of half.
 */
export const seizingLevel = (range: readonly [number, number]): number => range[0] + (range[1] - range[0]) / 2;

/** When a series first reaches `level`, linear between frames; null if it never does. */
export function firstCrossing(y: ArrayLike<number>, times: ArrayLike<number>, level: number): number | null {
  for (let i = 0; i < y.length; i++) {
    const v = y[i]!;
    if (!(v >= level)) continue;
    if (i === 0) return times[0]!;
    const u = y[i - 1]!;
    const w = Number.isFinite(u) && v > u ? (level - u) / (v - u) : 1;
    return times[i - 1]! + Math.min(1, Math.max(0, w)) * (times[i]! - times[i - 1]!);
  }
  return null;
}

/** The earliest any column of `[frame][key]` values reaches `level`. */
export function earliestCrossing(values: ArrayLike<number>, keys: number, times: ArrayLike<number>, level: number): number | null {
  let best: number | null = null;
  const col = new Float32Array(times.length);
  for (let k = 0; k < keys; k++) {
    for (let f = 0; f < times.length; f++) col[f] = values[f * keys + k]!;
    const t = firstCrossing(col, times, level);
    if (t !== null && (best === null || t < best)) best = t;
  }
  return best;
}

/**
 * A seizure run's onset where it marks none of its own: the first time any parcel's activity reaches
 * `seizingLevel`. Null for another kind of run (spreading depolarization is dynamics too), one without a
 * colour range or parcel activity, or one whose header has its own markers (a patient fit's `onset_s`).
 */
export async function seizureOnset(d: DynamicsSim): Promise<number | null> {
  const h = d.header;
  const range = d.activityRange;
  const own = !!h.scenario?.events?.length || !!h.scenario?.stimuli?.length || typeof h.onset_s === 'number';
  if (h.kind !== 'seizure' || own || !range || !d.has('parcel_activity')) return null;
  const P = d.parcels.length;
  const data = await d.array('parcel_activity');
  const times = frameTimes(d.axisOf('parcel_activity'));
  if (!P || data.length !== times.length * P) return null;
  return earliestCrossing(data, P, times, seizingLevel(range));
}

const SPECIES_WORDS = /^(Human|Mouse|Rat|Macaque|Fly)\s+(.+)$/;

/**
 * A simulated cell's heading (`cells.json`'s name, design's Neurons inspector): a leading species word goes
 * to the eyebrow, "Human L2/3 pyramidal cell 0603_11" → Human, "L2/3 pyramidal cell 0603_11".
 */
export function cellHeading(name: string): { species: string | null; title: string } {
  const m = SPECIES_WORDS.exec(name.trim());
  return m ? { species: m[1]!, title: m[2]! } : { species: null, title: name.trim() };
}

/** Spec §15's Type for a simulated cell: its type without the layer it starts with, then the layer ("L2/3 pyramidal" in L2/3 → "Pyramidal, layer 2/3"). */
export function simCellType(type: string | undefined, layer: string | undefined): string | null {
  if (!type) return null;
  const bare = layer && type.startsWith(`${layer} `) ? type.slice(layer.length + 1) : type;
  const noun = bare.replace(/^./, (c) => c.toUpperCase());
  return layer && layer !== 'unknown' ? `${noun}, ${layer === 'WM' ? 'white matter' : layer.replace(/^L(?=\d)/, 'layer ')}` : noun;
}

/** A cell's somatic spikes in the run, and its mean firing rate over the run, Hz. */
export function cellRate(spikes: { times: Float32Array; cell: Uint32Array } | null, cell: number, axis: TimeAxis): { spikes: number; hz: number; seconds: number } {
  const seconds = duration({ ...axis, periodic: false });
  let n = 0;
  if (spikes) for (let i = 0; i < spikes.cell.length; i++) if (spikes.cell[i] === cell && spikes.times[i]! >= axis.t0 - 1e-9 && spikes.times[i]! <= axis.t0 + seconds + 1e-9) n++;
  return { spikes: n, hz: seconds > 0 ? n / seconds : 0, seconds };
}
